/**
 * lpcHelper — helpers pra Universal LPC Spritesheet (versao 2024+).
 *
 * Os assets vem do projeto open-source LPC (Liberated Pixel Cup),
 * licenca CC-BY-SA 3.0. Servimos via GitHub Pages oficial do projeto
 * (que ja resolve CORS corretamente).
 *
 * Estrutura nova LPC:
 *  - Cada item tem subpasta com animacoes separadas (walk.png, slash.png, etc)
 *  - walk.png eh universal (todos items tem)
 *  - walk.png e 576x256 (9 cols x 4 linhas de 64x64): 4 direcoes (N/W/S/E),
 *    9 frames de animacao
 *  - Pra mostrar parado olhando pra frente: linha 2 (south), coluna 0
 *    -> backgroundPosition: 0 -128px (em scale 1)
 *
 * Body: body/bodies/male/idle.png (128x256 = 2x4 frames de 64x64,
 *   4 direcoes x 2 frames de respiracao)
 *
 * Pra simplicidade, vamos sempre usar walk.png e mostrar frame [0, 2]
 * (south facing, primeiro frame). Excecao: body que so tem idle.png
 * em alguns paths — vamos usar /idle.png mesmo e mostrar frame [0,2].
 */

// GitHub Pages oficial do projeto — funciona com CORS aberto
const LPC_BASE = 'https://liberatedpixelcup.github.io/Universal-LPC-Spritesheet-Character-Generator/spritesheets/';

// Frame: 64x64
const FRAME_W = 64;
const FRAME_H = 64;

// Direcoes LPC nas linhas do PNG:
// 0 = NORTH (costas), 64 = WEST, 128 = SOUTH (frente), 192 = EAST
export const DIRECTIONS = {
  NORTH: 0,
  WEST: 64,
  SOUTH: 128,
  EAST: 192
};

// Padrao: SOUTH (de frente). Pode ser sobrescrito via parametro `direction`.
const DEFAULT_FRAME_Y = 128; // SOUTH: personagem de frente (olhando pro aluno)
const FRAME_X = 0;

/**
 * Animacoes do pack LPC. Todas as pecas (corpo, roupa, cabelo, calcado,
 * cabeca, olhos) tem os mesmos arquivos — muda so o nome do PNG.
 * Dimensoes verificadas no CDN oficial:
 *   idle 128x256 (2 frames x 4 dir) · walk 576x256 (9x4) · run 512x256 (8x4)
 *   slash 384x256 (6x4) · thrust 512x256 (8x4) · spellcast 448x256 (7x4)
 *   hurt 384x64 (6 frames x 1 direcao!) · jump 320x256 (5x4)
 */
export const ANIMATIONS = {
  idle:      { file: 'idle',      frames: 2, dirs: 4, fps: 3,  loop: true },
  walk:      { file: 'walk',      frames: 9, dirs: 4, fps: 9,  loop: true },
  run:       { file: 'run',       frames: 8, dirs: 4, fps: 12, loop: true },
  slash:     { file: 'slash',     frames: 6, dirs: 4, fps: 12, loop: false },
  thrust:    { file: 'thrust',    frames: 8, dirs: 4, fps: 12, loop: false },
  spellcast: { file: 'spellcast', frames: 7, dirs: 4, fps: 10, loop: false },
  jump:      { file: 'jump',      frames: 5, dirs: 4, fps: 8,  loop: false },
  // hurt so existe virado pra frente (1 linha) — o frameY tem que ser 0
  hurt:      { file: 'hurt',      frames: 6, dirs: 1, fps: 8,  loop: false }
};

// Pecas geradas pelo Estilista IA ficam no backend
const AI_PREFIX = '/uploads/ai-clothes/';

/**
 * Troca a animacao no caminho do sprite. O pack tem dois formatos:
 *   a) .../male/walk.png            -> .../male/slash.png     (maioria)
 *   b) .../zombie/walk/zombie.png   -> .../zombie/slash/zombie.png
 * Pecas do Estilista IA seguem u<user>-<ts>-<anim>.png quando geradas com
 * animacoes; as antigas (arquivo unico) caem no fallback e ficam no walk.
 */
export function getAnimationLayer(lpcLayer, animation) {
  if (!lpcLayer) return null;
  // nome fora do mapa (ex: 'attack_slash' das armas) e usado como esta —
  // vem do animMap da peca e corresponde a uma pasta real do pack
  var anim = ANIMATIONS[animation] ? ANIMATIONS[animation].file : (animation || 'walk');

  // Peca do Estilista IA
  if (lpcLayer.indexOf(AI_PREFIX) !== -1) {
    var m = lpcLayer.match(/^(.*)-(idle|walk|run|slash|thrust|spellcast|jump|hurt)\.png$/);
    if (m) return m[1] + '-' + anim + '.png';
    return lpcLayer; // peca antiga: so tem o spritesheet unico
  }

  // Formato (b): pasta com o nome da animacao
  var pasta = lpcLayer.match(/^(.*)\/(idle|walk|run|slash|thrust|spellcast|jump|hurt|attack_slash|attack_backslash|attack_halfslash|combat_idle)\/([^/]+)\.png$/);
  if (pasta) return pasta[1] + '/' + anim + '/' + pasta[3] + '.png';

  // Formato (a): arquivo com o nome da animacao
  var arquivo = lpcLayer.match(/^(.*)\/(idle|walk|run|slash|thrust|spellcast|jump|hurt|attack_slash|combat_idle)\.png$/);
  if (arquivo) return arquivo[1] + '/' + anim + '.png';

  return lpcLayer;
}

/** Peca do Estilista IA que so tem o spritesheet walk (formato antigo) */
export function isSingleSheet(lpcLayer) {
  if (!lpcLayer) return true;
  if (lpcLayer.indexOf(AI_PREFIX) === -1) return false;
  return !/-(idle|walk|run|slash|thrust|spellcast|jump|hurt)\.png$/.test(lpcLayer);
}

/**
 * Estilo de uma camada animada.
 * @param {string} lpcLayer  caminho base da peca
 * @param {number} scale
 * @param {number} direction 0=N, 64=W, 128=S, 192=E
 * @param {string} animation chave de ANIMATIONS
 * @param {number} frame     indice do frame (0..frames-1)
 */
export function getAnimatedLayerStyle(lpcLayer, scale, direction, animation, frame, animMap) {
  scale = scale || 4;
  var spec = ANIMATIONS[animation] || ANIMATIONS.idle;
  var dir = (typeof direction === 'number') ? direction : DEFAULT_FRAME_Y;

  var layer = lpcLayer;
  var f = frame || 0;
  var anim = animation;

  // Armas e escudos nao tem todas as animacoes do corpo e usam outros nomes
  // (attack_slash). O mapa traduz; null = a peca some naquela pose.
  if (animMap) {
    if (typeof animMap === 'string') {
      try { animMap = JSON.parse(animMap); } catch (e) { animMap = null; }
    }
    if (animMap && Object.prototype.hasOwnProperty.call(animMap, animation)) {
      if (!animMap[animation]) return { display: 'none' };
      anim = animMap[animation];
    }
  }

  // Peca sem as outras animacoes: mostra o frame parado do sheet que tem
  if (isSingleSheet(lpcLayer)) {
    f = 0;
  } else {
    layer = getAnimationLayer(lpcLayer, anim);
  }

  // A pose mapeada pode ter menos frames que a do corpo (ex: escudo no idle
  // usa o walk) — segura o indice pra nao cair fora do spritesheet
  var alvo = ANIMATIONS[anim];
  if (alvo && f >= alvo.frames) f = alvo.frames - 1;

  // hurt tem uma linha so (sempre de frente)
  var specReal = ANIMATIONS[anim] || spec;
  var frameY = (specReal.dirs === 1 && !isSingleSheet(lpcLayer)) ? 0 : dir;
  var frameX = f * FRAME_W;

  var url = getLpcUrl(layer);
  if (!url) return { display: 'none' };

  return {
    backgroundImage: 'url(' + url + ')',
    backgroundPosition: '-' + frameX + 'px -' + frameY + 'px',
    backgroundSize: 'auto auto',
    backgroundRepeat: 'no-repeat',
    imageRendering: 'pixelated',
    position: 'absolute',
    top: 0,
    left: 0,
    transform: 'scale(' + scale + ')',
    transformOrigin: '0 0',
    width: FRAME_W + 'px',
    height: FRAME_H + 'px'
  };
}

/**
 * Constroi URL completa do PNG dado o path relativo.
 */
export function getLpcUrl(lpcLayer) {
  if (!lpcLayer) return null;
  if (lpcLayer.startsWith('http://') || lpcLayer.startsWith('https://')) return lpcLayer;
  // Pecas geradas pelo Estilista IA ficam hospedadas no proprio backend
  if (lpcLayer.startsWith('/uploads/ai-clothes/')) {
    return (sessionStorage.getItem('backend') || '') + lpcLayer;
  }
  var clean = lpcLayer.replace(/^\/uploads\/lpc\//, '').replace(/^\//, '');
  return LPC_BASE + clean;
}

/**
 * Estilo CSS pra renderizar uma camada como sprite individual.
 * @param {string} lpcLayer
 * @param {number} scale
 * @param {number} [frameY] - Y do frame (0=N, 64=W, 128=S, 192=E)
 */
export function getLpcLayerStyle(lpcLayer, scale, frameY) {
  scale = scale || 4;
  var fy = (typeof frameY === 'number') ? frameY : DEFAULT_FRAME_Y;
  var url = getLpcUrl(lpcLayer);
  if (!url) return { display: 'none' };

  return {
    backgroundImage: 'url(' + url + ')',
    backgroundPosition: '-' + FRAME_X + 'px -' + fy + 'px',
    backgroundSize: 'auto auto',
    backgroundRepeat: 'no-repeat',
    imageRendering: 'pixelated',
    position: 'absolute',
    top: 0,
    left: 0,
    transform: 'scale(' + scale + ')',
    transformOrigin: '0 0',
    width: FRAME_W + 'px',
    height: FRAME_H + 'px'
  };
}

/**
 * Determina qual linha do spritesheet (frameY) mostrar no preview baseado no slot.
 * Items que ficam no rosto (eyes/beard/glasses/mustache) precisam mostrar a linha
 * south (Y=128) onde o rosto eh visivel. Items grandes mostram norte (Y=0).
 */
function getPreviewFrameY(slot) {
  // Slots de rosto/face precisam do frame south pra mostrar conteudo
  // Todos os slots ficam mais reconheciveis de frente (rosto, roupa, cabelo)
  return 128;
}

/**
 * Estilo pra preview pequeno num card de inventario.
 * @param {string} lpcLayer
 * @param {number} size
 * @param {string} [slot] - usado pra escolher melhor frame visivel
 */
export function getLpcPreviewStyle(lpcLayer, size, slot) {
  size = size || 80;
  var url = getLpcUrl(lpcLayer);
  if (!url) return { display: 'none' };

  var fy = slot ? getPreviewFrameY(slot) : DEFAULT_FRAME_Y;

  // Calcula o scale pra que o frame de 64x64 caiba em "size"
  var scale = size / FRAME_W;

  return {
    width: FRAME_W + 'px',
    height: FRAME_H + 'px',
    backgroundImage: 'url(' + url + ')',
    backgroundPosition: '-' + FRAME_X + 'px -' + fy + 'px',
    backgroundSize: 'auto auto',
    backgroundRepeat: 'no-repeat',
    imageRendering: 'pixelated',
    transform: 'scale(' + scale + ')',
    transformOrigin: '0 0',
    display: 'inline-block'
  };
}

export default {
  getLpcUrl,
  getLpcLayerStyle,
  getLpcPreviewStyle,
  getAnimatedLayerStyle,
  getAnimationLayer,
  isSingleSheet,
  ANIMATIONS
};
